import { z } from "zod";

/**
 * Utility helpers for working with UTM attribution parameters.
 *
 * Common aliases: utm, tracking, attribution.
 */
export type RawUtmParameters = Readonly<Record<string, string>>;

export type NormalizedUtmEntry = readonly [string, string];

const UTM_PREFIX = "utm";

const normalizeKey = (key: string): string | null => {
  const trimmed = key.trim();
  if (trimmed === "") {
    return null;
  }

  const lowered = trimmed.toLowerCase();
  return lowered.startsWith(UTM_PREFIX) ? lowered : null;
};

const normalizeValue = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

const utmParametersBaseSchema = z
  .record(
    z.string(),
    z.string().trim().min(1, "UTM parameter values must be non-empty"),
  )
  .superRefine(
  (parameters, context) => {
    Object.keys(parameters).forEach((key) => {
      if (!normalizeKey(key)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "UTM parameter keys must start with 'utm' and be non-empty",
          path: [key],
        });
      }
    });
  },
);

export const utmParametersSchema = utmParametersBaseSchema.optional();

export function normalizeUtmEntries(
  entries: Iterable<readonly [string, string]> | null | undefined,
): NormalizedUtmEntry[] {
  if (!entries) {
    return [];
  }

  return Array.from(entries)
    .map(([rawKey, rawValue]): NormalizedUtmEntry | null => {
      const key = normalizeKey(rawKey);
      const value = normalizeValue(rawValue);
      return key && value ? [key, value] : null;
    })
    .filter((entry): entry is NormalizedUtmEntry => entry !== null)
    .sort((first, second) => first[0].localeCompare(second[0]));
}

export function normalizeUtmEntriesFromRecord(
  parameters: RawUtmParameters | null | undefined,
): NormalizedUtmEntry[] {
  if (!parameters) {
    return [];
  }

  return normalizeUtmEntries(Object.entries(parameters));
}

export function utmEntriesToRecord(
  entries: readonly NormalizedUtmEntry[],
): RawUtmParameters {
  return Object.fromEntries(entries) as RawUtmParameters;
}

export function mapToUtmRecord(
  map: ReadonlyMap<string, string> | null | undefined,
): RawUtmParameters | null {
  const entries = normalizeUtmEntries(map?.entries() ?? null);
  if (entries.length === 0) {
    return null;
  }

  return utmEntriesToRecord(entries);
}

export function findUtmSource(
  entries: readonly NormalizedUtmEntry[],
): string | null {
  const match = entries.find(([key]) => key === "utm_source");
  return match ? match[1] : null;
}

export function buildUtmTrackingQuery(
  entries: readonly NormalizedUtmEntry[],
): string | null {
  if (entries.length === 0) {
    return null;
  }

  return entries
    .map(([key, value]) =>
      `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join("&");
}
